import { Box, Container, Link, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';

import SEOHead from 'components/SEOHead';

export type LegalSlug =
  | 'politica-de-privacidade'
  | 'termos-da-assinatura'
  | 'termos-de-uso';

interface LegalDocumentMeta {
  title: string;
  description: string;
  file: string;
}

export const LEGAL_DOCUMENTS: Record<LegalSlug, LegalDocumentMeta> = {
  'politica-de-privacidade': {
    title: 'Política de Privacidade',
    description:
      'Como o Clube da 42 coleta, utiliza e protege dados pessoais de clientes e assinantes.',
    file: '/legal/politica-de-privacidade.md',
  },
  'termos-da-assinatura': {
    title: 'Termos da Assinatura',
    description:
      'Condições específicas dos planos de assinatura do Clube da 42.',
    file: '/legal/termos-da-assinatura.md',
  },
  'termos-de-uso': {
    title: 'Termos de Uso e Condições de Compra',
    description:
      'Regras para uso do site e compra de cervejas, growlers e outros produtos do Clube da 42.',
    file: '/legal/termos-de-uso-e-condicoes-de-compra.md',
  },
};

export const LEGAL_FOOTER_LINKS: { slug: LegalSlug; label: string }[] = [
  {
    slug: 'politica-de-privacidade',
    label: 'Política de Privacidade',
  },
  {
    slug: 'termos-da-assinatura',
    label: 'Termos da Assinatura',
  },
  {
    slug: 'termos-de-uso',
    label: 'Termos de Uso e Condições de Compra',
  },
];

interface LegalDocumentProps {
  slug: LegalSlug;
}

const markdownSx = {
  '& h1': {
    fontSize: { xs: '1.75rem', md: '2.25rem' },
    fontWeight: 800,
    mb: 1,
    color: 'text.primary',
  },
  '& h2': {
    fontSize: { xs: '1.25rem', md: '1.5rem' },
    fontWeight: 700,
    mt: 4,
    mb: 1.5,
    color: 'text.primary',
  },
  '& h3': {
    fontSize: '1.1rem',
    fontWeight: 700,
    mt: 3,
    mb: 1,
  },
  '& p': {
    mb: 1.5,
    lineHeight: 1.7,
    color: 'text.primary',
  },
  '& ul, & ol': {
    pl: 3,
    mb: 2,
  },
  '& li': {
    mb: 0.75,
    lineHeight: 1.7,
  },
  '& strong': {
    fontWeight: 700,
  },
  '& a': {
    color: 'primary.main',
  },
};

const LegalDocument: React.FC<LegalDocumentProps> = ({ slug }) => {
  const meta = LEGAL_DOCUMENTS[slug];
  const [content, setContent] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setContent('');
    setError(false);

    fetch(meta.file)
      .then(response => {
        if (!response.ok) {
          throw new Error('Documento não encontrado');
        }
        return response.text();
      })
      .then(text => {
        if (!cancelled) setContent(text);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [meta.file]);

  return (
    <Box sx={{ minHeight: '60vh', py: { xs: 4, md: 6 } }}>
      <SEOHead
        title={`${meta.title} | Clube da 42`}
        description={meta.description}
      />
      <Container maxWidth="md">
        {error && (
          <Typography color="error">
            Não foi possível carregar este documento. Tente novamente.
          </Typography>
        )}
        {!error && !content && (
          <Typography color="text.secondary">Carregando...</Typography>
        )}
        {content && (
          <Box sx={markdownSx}>
            <ReactMarkdown>{content}</ReactMarkdown>
          </Box>
        )}
        <Box
          sx={{
            mt: 6,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {LEGAL_FOOTER_LINKS.filter(item => item.slug !== slug).map(item => (
            <Link
              key={item.slug}
              component={RouterLink}
              to={`/${item.slug}`}
              underline="hover"
            >
              {item.label}
            </Link>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default LegalDocument;
